'use client';
/**
 * components/Notices.tsx — ce que l'application a à te dire.
 *
 * La table `notifications` et son routeur tRPC existaient depuis le début, mais
 * aucun écran ne les lisait : un paiement encaissé ne prévenait donc personne,
 * et le mécano découvrait sa mission en rouvrant l'application par hasard.
 *
 * MÊME PLACEMENT QUE `SessionAlert`, et pour la même raison : ces messages
 * valent pour le COMPTE, pas pour l'écran où l'on se trouve. Posés sur le
 * layout, ils suivent le compte d'un pôle à l'autre.
 *
 * CE N'EST PAS UNE NOTIFICATION SYSTÈME. Rien ne sonne, rien n'apparaît hors de
 * l'application : c'est un bandeau interne, qui n'exige aucune permission
 * navigateur ni service worker. Le jour où les notifications natives arrivent
 * (Capacitor), elles s'ajouteront à côté — la table restera la source.
 *
 * ELLE DIT, ELLE NE MONTRE PAS. Elle a porté un moment le récapitulatif complet,
 * déplié sur place. C'était une surface de travail posée sur la surface de
 * travail : pour agir il faut la fiche, et la fiche vit là où l'intervention se
 * trouve — au pôle Agenda du mécano, dans « Mes demandes » côté client, tous deux
 * signalés par un point rouge. Ce bandeau annonce, puis s'efface.
 *
 * Le sondage est volontairement lent (20 s) : ces messages ne sont pas des
 * secondes qui comptent, et un intervalle court réveille l'appareil pour rien.
 */

import { useEffect, useRef, useState } from 'react';
import { useCompte } from '@/lib/auth/client';

import { trpc } from '@/lib/trpc/client';
import { topBanner } from './topBanner';

const stroke = {
  fill: 'none', stroke: 'currentColor', strokeWidth: 2.2,
  strokeLinecap: 'round', strokeLinejoin: 'round',
} as const;

/** L'icône dit d'un coup d'œil DE QUOI ça parle, avant même de lire le titre —
 *  un même verrou pour « créneau accepté » et « payé » les rendait
 *  indistinguables au survol rapide (signalé le 25/08). */
function NoticeIcon({ type }: { type: string }) {
  if (type === 'offer_accepted') // un rendez-vous ferme est posé
    return <svg width="17" height="17" viewBox="0 0 24 24" {...stroke} aria-hidden><rect x="3" y="5" width="18" height="16" rx="2.5" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>;
  if (type === 'offer_accepted_now') // rien de fixé dans le temps, juste « c'est retenu »
    return <svg width="17" height="17" viewBox="0 0 24 24" {...stroke} aria-hidden><path d="M13 2 3 14h9l-1 8 10-12h-9z" /></svg>;
  if (type === 'payment_held') // l'argent a bougé
    return <svg width="17" height="17" viewBox="0 0 24 24" {...stroke} aria-hidden><rect x="2.5" y="6.5" width="19" height="13" rx="2.5" /><circle cx="12" cy="13" r="3" /></svg>;
  if (type === 'wallet_credit') // l'argent est ARRIVÉ — un billet, pas un coffre
    return <svg width="17" height="17" viewBox="0 0 24 24" {...stroke} aria-hidden><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.6" /><path d="M6 12h.01M18 12h.01" /></svg>;
  // Défaut : le séquestre, pour tout le reste (promo, devis, pièce…).
  return <svg width="17" height="17" viewBox="0 0 24 24" {...stroke} aria-hidden><rect x="4" y="10.5" width="16" height="10" rx="2.5" /><path d="M8 10.5V7a4 4 0 1 1 8 0v3.5" /></svg>;
}

export function Notices() {
  const { isSignedIn } = useCompte();
  const utils = trpc.useUtils();
  const [écartées, setÉcartées] = useState<string[]>([]);
  const [closing, setClosing] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const q = trpc.notifications.list.useQuery(
    { limit: 5, unread_only: true },
    { enabled: !!isSignedIn, refetchInterval: 20_000, retry: false },
  );
  const markRead = trpc.notifications.markRead.useMutation({
    onSettled: () => void utils.notifications.list.invalidate(),
  });

  // Une seule à la fois : empiler trois bandeaux sur un écran de téléphone
  // recouvre ce qu'on était en train de faire. La suivante prend la place dès
  // que celle-ci est lue.
  const n = (q.data?.notifications ?? []).find(x => !écartées.includes(x.id));
  const noticeId = n?.id;
  const noticeMessage = n?.message;

  const fermer = () => {
    if (!n) return;
    // Le retrait rejoue la même sortie que SessionAlert (glisse vers le haut,
    // s'efface) : ça se fait par où c'est venu, pas par les côtés.
    setClosing(true);
    setTimeout(() => {
      setÉcartées(prev => [...prev, n.id]);
      // L'ARGENT REÇU NE SE MARQUE PAS LU ICI (03/09). Ce bandeau s'efface tout
      // seul au bout de 5 s : le marquer lu éteindrait du même coup la pastille
      // du portefeuille, qui compte justement les crédits pas encore regardés.
      // Le mécano peut être en train de visser quand il passe. Seule l'ouverture
      // du portefeuille l'éteint (`markAllRead({ type: 'wallet_credit' })`).
      if (n.type !== 'wallet_credit') markRead.mutate({ id: n.id });
      setClosing(false);
    }, 260);
  };

  const reprendreConstat = () => {
    if (!n) return;
    markRead.mutate({ id: n.id });
    const base = n.action_url ?? '/prestataire';
    const url = n.related_id
      ? `${base}${base.includes('?') ? '&' : '?'}intervention=${encodeURIComponent(n.related_id)}`
      : base;
    window.location.assign(url);
  };

  // S'efface seule au bout de 5 s : ce bandeau annonce, il n'attend pas qu'on
  // le lise pour de bon (signalé le 25/08 — il restait plaqué là indéfiniment).
  useEffect(() => {
    if (!n) return;
    const t = window.setTimeout(fermer, n.type === 'report_draft_reminder' ? 12_000 : 5000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n?.id]);

  // S'annonce auprès de la bascule jour/nuit tant qu'il est affiché, pour
  // qu'elle se range dessous au lieu de passer par-dessus la croix.
  useEffect(() => {
    if (!noticeId || !cardRef.current) { topBanner.retirer('notices'); return; }
    const h = cardRef.current.offsetHeight;
    topBanner.poser('notices', h);
    return () => topBanner.retirer('notices');
  }, [noticeId, noticeMessage]);

  if (!n) return null;

  // AU-DESSUS DE TOUT (26/08). `z-[60]` passait sous les feuillets, qui vivent à
  // 4000 et 4200 : on voyait la page bouger derrière la modale sans jamais lire
  // le message. Une notification informe sur l'instant — si elle arrive trop
  // tard pour être lue, elle n'a servi à rien.
  return (
    <div className="fixed inset-x-0 top-0 z-[5000] flex justify-center px-3 pt-[max(env(safe-area-inset-top),10px)] pointer-events-none">
      <div ref={cardRef}
        className={`drop-in pointer-events-auto w-full max-w-[420px] rounded-2xl p-3.5
          transition-all duration-[260ms] ease-out ${closing ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'}`}
        style={{ background: 'var(--s-sheet)', boxShadow: 'var(--sh-card)' }}>
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 shrink-0 rounded-xl bg-emerald-500/15 text-[var(--accent-ink)] flex items-center justify-center">
            <NoticeIcon type={n.type} />
          </span>

          <div className="min-w-0 flex-1">
            {/* Le montant reçu se lit en gros et en vert : c'est la seule
                notification qui annonce une entrée d'argent, elle n'a pas à
                ressembler à un rappel de rendez-vous. */}
            <div className={n.type === 'wallet_credit'
              ? 'text-[19px] font-black tabular-nums text-[var(--accent-ink)]'
              : 'text-[13px] font-black text-[var(--ink-1)]'}>{n.title}</div>
            {n.message && (
              <div className="mt-0.5 text-[12px] leading-snug text-[var(--ink-3)]">{n.message}</div>
            )}
          </div>

          <button type="button" onClick={fermer} disabled={closing} aria-label="Fermer"
            className="emboss relative h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-red-500">
            <span className="relative">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.6" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
            </span>
          </button>
          </div>
        {n.type === 'report_draft_reminder' && (
          <button type="button" onClick={reprendreConstat} disabled={closing}
            className="mt-3 w-full rounded-xl bg-[var(--accent-ink)] py-2.5 text-xs font-black text-white">
            Reprendre le constat
          </button>
        )}
        </div>
      </div>
  );
}

export default Notices;
